/**
 * @copyright 2024-2026 David Baugh
 * @license Proprietary - All Rights Reserved
 * Unauthorized copying, modification, or distribution is strictly prohibited.
 */

import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { useToast } from '../ui/use-toast';
import { Upload, ImageIcon, Loader2, X, Tag } from 'lucide-react';
import ImageGallery from './ImageGallery';
import TeacherImageGallery from '../teacher/ImageGallery';

const ImageSelector = ({ 
  currentImageUrl, 
  onImageSelect, 
  onImageRemove, 
  teacherContext = false,
  disabled = false,
  shouldRefreshOnFocus = null
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Handle dialog state changes
  const handleSetIsOpen = (value) => {
    
    // Disable window focus refresh while dialog is open
    if (shouldRefreshOnFocus) {
      if (value) {
        // Dialog opening - disable refresh
        shouldRefreshOnFocus.current = false;
      } else {
        // Dialog closing - re-enable refresh after a delay
        setTimeout(() => {
          shouldRefreshOnFocus.current = true;
        }, 1000); // 1 second delay to avoid immediate refresh
      }
    }
    
    setIsOpen(value);
  };
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploadData, setUploadData] = useState({
    title: '',
    description: '',
    altText: '',
    tags: []
  });
  const [newTag, setNewTag] = useState('');
  const { toast } = useToast();

  // Handle file selection for preview
  const handleFileSelect = (file) => {
    
    // Clean up previous preview URL
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    
    setSelectedFile(file);
    
    // Auto-populate title with filename (user can edit)
    setUploadData(prev => ({ 
      ...prev, 
      title: prev.title || file.name.replace(/\.[^/.]+$/, '') // Remove file extension
    }));
    
    // Create preview URL
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  };

  // Handle actual upload (after user confirms)
  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', selectedFile);
      formData.append('title', uploadData.title);
      formData.append('description', uploadData.description);
      formData.append('altText', uploadData.altText);
      uploadData.tags.forEach(tag => formData.append('tags[]', tag));

      const uploadEndpoint = teacherContext 
        ? `${process.env.REACT_APP_API_URL}/api/teacher/images`
        : `${process.env.REACT_APP_API_URL}/api/admin/images`;

      const response = await fetch(uploadEndpoint, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (response.ok) {
        const result = await response.json();
        const imageUrl = `${process.env.REACT_APP_API_URL}${result.imageUrl}`;
        
        onImageSelect(imageUrl, result.image);
        handleSetIsOpen(false);
        
        // Clean up upload state
        setUploadData({ title: '', description: '', altText: '', tags: [] });
        setSelectedFile(null);
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
          setPreviewUrl(null);
        }
        
        toast({
          title: "Success",
          description: "Image uploaded successfully"
        });
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Error",
        description: "Failed to upload image",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  // Handle selection from gallery
  const handleGallerySelect = (image) => {
    const imageUrl = `${process.env.REACT_APP_API_URL}${image.file_path}`;
    onImageSelect(imageUrl, image);
    handleSetIsOpen(false);
  };

  // Add tag to upload data
  const addTag = () => {
    if (newTag.trim() && !uploadData.tags.includes(newTag.trim())) {
      setUploadData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  // Remove tag from upload data
  const removeTag = (tagToRemove) => {
    setUploadData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  return (
    <div className="space-y-2">
      <Label>Image (Optional)</Label>
      
      {/* Current Image Preview */}
      {currentImageUrl && (
        <div className="mb-3">
          <p className="text-sm text-muted-foreground mb-2">Current Image:</p>
          <div className="relative inline-block">
            <img 
              src={currentImageUrl} 
              alt="Question preview" 
              className="max-w-full h-auto max-h-48 rounded border shadow-sm"
              onError={() => {
                onImageRemove();
                toast({
                  title: "Error",
                  description: "Failed to load image preview",
                  variant: "destructive"
                });
              }}
            />
            <Button
              type="button"
              variant="destructive"
              size="sm"
              className="absolute top-2 right-2"
              onClick={onImageRemove}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Image Selection Button */}
      <div>
        <Button 
          type="button" 
          variant="outline" 
          disabled={disabled}
          onClick={() => handleSetIsOpen(true)}
          className="w-full"
        >
          <ImageIcon className="h-4 w-4 mr-2" />
          {currentImageUrl ? 'Change Image' : 'Select Image'}
        </Button>
      </div>

      {/* Image Selection Dialog */}
      <Dialog open={isOpen} onOpenChange={handleSetIsOpen}>
        <DialogContent className="max-w-4xl min-h-[70vh] max-h-[90vh] overflow-auto relative">
          <DialogHeader>
            <DialogTitle>Select or Upload Image</DialogTitle>
          </DialogHeader>
            
            <Tabs defaultValue="gallery" className="w-full h-full">
                <TabsList className="grid w-full grid-cols-2 mb-4 bg-gray-100 p-1 rounded-lg">
                  <TabsTrigger 
                    value="gallery"
                    className="data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md transition-all duration-200 font-semibold rounded-md py-2"
                  >
                    Select from Gallery
                  </TabsTrigger>
                  <TabsTrigger 
                    value="upload"
                    className="data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md transition-all duration-200 font-semibold rounded-md py-2"
                  >
                    Upload New Image
                  </TabsTrigger>
                </TabsList>
              
              <TabsContent value="gallery" className="h-[600px] overflow-auto">
                {teacherContext ? (
                  <TeacherImageGallery 
                    selectMode={true}
                    onImageSelect={handleGallerySelect}
                  />
                ) : (
                  <ImageGallery 
                    selectMode={true}
                    onImageSelect={handleGallerySelect}
                  />
                )}
              </TabsContent>
              
              <TabsContent value="upload" className="space-y-4">
                <div className="max-w-md mx-auto space-y-4">
                  <div>
                    <Label htmlFor="image-file">Select Image File</Label>
                    <Input
                      id="image-file"
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        if (e.target.files[0]) {
                          handleFileSelect(e.target.files[0]);
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      disabled={uploading}
                    />
                  </div>
                  
                  {/* Image Preview */}
                  {previewUrl && (
                    <div>
                      <Label>Preview</Label>
                      <div className="border rounded-lg p-4 bg-gray-50">
                        <img 
                          src={previewUrl} 
                          alt="Preview" 
                          className="max-w-full h-auto max-h-48 rounded border mx-auto block"
                        />
                        <p className="text-sm text-muted-foreground mt-2 text-center">
                          {selectedFile?.name} ({Math.round(selectedFile?.size / 1024)}KB)
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Title Field - only show when file is selected */}
                  {selectedFile && (
                    <div>
                      <Label htmlFor="title">Title (Optional)</Label>
                      <Input
                        id="title"
                        value={uploadData.title}
                        onChange={(e) => setUploadData(prev => ({ ...prev, title: e.target.value }))}
                        placeholder={selectedFile?.name || "Enter image title..."}
                        disabled={uploading}
                      />
                    </div>
                  )}
                  
                  <div>
                    <Label htmlFor="description">Description (Optional)</Label>
                    <Textarea
                      id="description"
                      value={uploadData.description}
                      onChange={(e) => setUploadData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Brief description of the image..."
                      disabled={uploading}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="alt-text">Alt Text (Optional)</Label>
                    <Input
                      id="alt-text"
                      value={uploadData.altText}
                      onChange={(e) => setUploadData(prev => ({ ...prev, altText: e.target.value }))}
                      placeholder="Alternative text for accessibility..."
                      disabled={uploading}
                    />
                  </div>
                  
                  <div>
                    <Label>Tags (Optional)</Label>
                    <div className="flex space-x-2 mb-2">
                      <Input
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        placeholder="Add a tag..."
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                        disabled={uploading}
                      />
                      <Button 
                        type="button" 
                        onClick={addTag} 
                        size="sm"
                        disabled={uploading}
                      >
                        <Tag className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {uploadData.tags.map((tag, index) => (
                        <Badge 
                          key={index} 
                          variant="secondary" 
                          className="cursor-pointer" 
                          onClick={() => !uploading && removeTag(tag)}
                        >
                          {tag} <X className="h-3 w-3 ml-1" />
                        </Badge>
                      ))}
                    </div>
                  </div>
                  
                  {/* Upload Button */}
                  {selectedFile && (
                    <Button 
                      onClick={handleUpload}
                      disabled={uploading}
                      className="w-full"
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          Upload Image
                        </>
                      )}
                    </Button>
                  )}
                  
                  {uploading && (
                    <div className="text-center py-4">
                      <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">Uploading image...</p>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </DialogContent>
        </Dialog>
      
      {uploading && (
        <p className="text-sm text-muted-foreground flex items-center">
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          Uploading image...
        </p>
      )}
    </div>
  );
};

export default ImageSelector;